package webcomponents

import analyticsdashboard.ActiveHistoryPathSearchFieldValuesStore
import analyticsdashboard.ActiveHistoryPathSearchKeywordsStore
import analyticsdashboard.ActiveHistoryPathSearchObjectTypesStore
import analyticsdashboard.ActiveHistoryPathSearchReadOnlyKeywordsStore
import apiclient.customfields.FieldValue
import apiclient.geoobjects.ReadOnlyTags
import data.heatmaplayer.ActiveHeatmapLayerDefinitionStore
import data.keywordlayer.ActiveKeywordLayerDefinitionStore
import data.objects.ActiveObjectKeywordsStore
import data.objects.CurrentActiveFieldValueStore
import dev.fritz2.core.HtmlTag
import dev.fritz2.core.Id
import dev.fritz2.core.RenderContext
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.Store
import dev.fritz2.core.storeOf
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import map.views.workplacetools.ActiveArchetypeSearchFieldValuesStore
import map.views.workplacetools.ActiveArchetypeSearchKeywordsStore
import modals.twFieldValueModal
import model.KeywordTag
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import search.global.ActiveSearchFieldValuesStore
import search.global.ActiveSearchKeywordsStore
import search.nestedObjects.ActiveNestedSearchFieldValuesStore
import search.nestedObjects.ActiveNestedSearchKeywordStore
import theme.FormationUIIcons
import twcomponents.twIconMedium
import twcomponents.twRowOfNoGap
import utils.sortKeywordTags

enum class KeywordTagType {
    SearchTag,
    ObjectTag,
    AnalyticsPathViewTag,
    ArchetypeTag,
    NestedObjectTag,
    HeatmapLayerTag,
    KeywordLayerTag
}

enum class KeywordTagActionType {
    Default,
    Add,
    Remove,
    ReadOnly,
    RemoveReadOnly,
    DefaultFieldValue,
    AddFieldValue,
    RemoveFieldValue
}

enum class KeywordTagSize { Tiny, Normal }

/* ---------------------------------------------------------------------------
 * Data + handler helpers
 * -------------------------------------------------------------------------*/
data class TagHandlers(
    val addKeyword: SimpleHandler<String>? = null,
    val removeKeyword: SimpleHandler<String>? = null,
    val addOrUpdateFieldValue: SimpleHandler<FieldValue?>? = null,
    val removeFieldValue: SimpleHandler<FieldValue?>? = null,
    val addObjectType: SimpleHandler<String?>? = null,
    val removeObjectType: SimpleHandler<String?>? = null,
    val addReadOnlyKeyword: SimpleHandler<KeywordTag>? = null,
    val removeReadOnlyKeyword: SimpleHandler<KeywordTag>? = null,
    val addToSearchKeywords: SimpleHandler<String>? = null,
    val addToSearchObjectTypes: SimpleHandler<String?>? = null,
    val addToSearchReadOnlyKeywords: SimpleHandler<KeywordTag>? = null,
    val addToSearchFieldValues: SimpleHandler<FieldValue?>? = null,
)

fun KeywordTagType.handlers(): TagHandlers = withKoin {
    /* all store injections go here – kept exactly as in your original file */
    val currentActiveFieldValueStore: CurrentActiveFieldValueStore = get()
    val activeObjectKeywordsStore: ActiveObjectKeywordsStore = get()
    val activeSearchFieldValuesStore: ActiveSearchFieldValuesStore = get()
    val activeSearchKeywordsStore: ActiveSearchKeywordsStore = get()
    val activeArchetypeFieldValues: ActiveArchetypeSearchFieldValuesStore = get()
    val activeArchetypeKeywords: ActiveArchetypeSearchKeywordsStore = get()
    val activeKeywordLayerDefStore: ActiveKeywordLayerDefinitionStore = get()
    val activeHeatmapLayerDefStore: ActiveHeatmapLayerDefinitionStore = get()
    val activeHistoryKeywords: ActiveHistoryPathSearchKeywordsStore = get()
    val activeHistoryFieldValues: ActiveHistoryPathSearchFieldValuesStore = get()
    val activeHistoryObjectTypes: ActiveHistoryPathSearchObjectTypesStore = get()
    val activeHistoryReadOnly: ActiveHistoryPathSearchReadOnlyKeywordsStore = get()
    val activeNestedKeywords: ActiveNestedSearchKeywordStore = get()
    val activeNestedFieldValues: ActiveNestedSearchFieldValuesStore = get()

    return when (this@handlers) {
        KeywordTagType.ObjectTag -> TagHandlers(
            addKeyword = activeObjectKeywordsStore.directEditAddKeywordToActiveObject,
            removeKeyword = activeObjectKeywordsStore.directEditRemoveKeywordFromActiveObject,
            addOrUpdateFieldValue = currentActiveFieldValueStore.update,
            removeFieldValue = activeObjectKeywordsStore.directEditRemoveFieldValueFromObject,
            addToSearchKeywords = activeObjectKeywordsStore.addToActiveSearchKeywords,
            addToSearchObjectTypes = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
            addToSearchReadOnlyKeywords = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            addToSearchFieldValues = activeSearchFieldValuesStore.add,
        )

        KeywordTagType.SearchTag -> TagHandlers(
            addKeyword = activeSearchKeywordsStore.add,
            removeKeyword = activeSearchKeywordsStore.remove,
            addOrUpdateFieldValue = currentActiveFieldValueStore.update,
            removeFieldValue = activeSearchFieldValuesStore.remove,
            addObjectType = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
            removeObjectType = activeObjectKeywordsStore.removeFromActiveSearchObjectTypes,
            addReadOnlyKeyword = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            removeReadOnlyKeyword = activeObjectKeywordsStore.removeFromActiveSearchReadOnlyTags,
            addToSearchKeywords = activeObjectKeywordsStore.addToActiveSearchKeywords,
            addToSearchObjectTypes = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
            addToSearchReadOnlyKeywords = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            addToSearchFieldValues = activeSearchFieldValuesStore.add,
        )

        KeywordTagType.AnalyticsPathViewTag -> TagHandlers(
            addKeyword = activeHistoryKeywords.add,
            removeKeyword = activeHistoryKeywords.remove,
            addOrUpdateFieldValue = currentActiveFieldValueStore.update,
            removeFieldValue = activeHistoryFieldValues.remove,
            addObjectType = activeHistoryObjectTypes.add,
            removeObjectType = activeHistoryObjectTypes.remove,
            addReadOnlyKeyword = activeHistoryReadOnly.add,
            removeReadOnlyKeyword = activeHistoryReadOnly.remove,
            addToSearchKeywords = activeObjectKeywordsStore.addToActiveSearchKeywords,
            addToSearchObjectTypes = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
            addToSearchReadOnlyKeywords = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            addToSearchFieldValues = activeSearchFieldValuesStore.add,
        )

        KeywordTagType.ArchetypeTag -> TagHandlers(
            addKeyword = activeArchetypeKeywords.add,
            removeKeyword = activeArchetypeKeywords.remove,
            addOrUpdateFieldValue = currentActiveFieldValueStore.update,
            removeFieldValue = activeArchetypeFieldValues.remove,
            addToSearchKeywords = activeObjectKeywordsStore.addToActiveSearchKeywords,
            addToSearchObjectTypes = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
            addToSearchReadOnlyKeywords = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            addToSearchFieldValues = activeSearchFieldValuesStore.add,
        )

        KeywordTagType.NestedObjectTag -> TagHandlers(
            addKeyword = activeNestedKeywords.add,
            removeKeyword = activeNestedKeywords.remove,
            addOrUpdateFieldValue = currentActiveFieldValueStore.update,
            removeFieldValue = activeNestedFieldValues.remove,
            addObjectType = activeObjectKeywordsStore.addToActiveNestedObjectTypesWithoutRouting,
            removeObjectType = activeObjectKeywordsStore.removeFromActiveNestedObjectTypes,
            addReadOnlyKeyword = activeObjectKeywordsStore.addToActiveNestedObjectReadOnlyTags,
            removeReadOnlyKeyword = activeObjectKeywordsStore.removeFromActiveNestedObjectReadOnlyTags,
            addToSearchKeywords = activeObjectKeywordsStore.addToActiveSearchKeywords,
            addToSearchObjectTypes = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
            addToSearchReadOnlyKeywords = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            addToSearchFieldValues = activeSearchFieldValuesStore.add,
        )

        KeywordTagType.HeatmapLayerTag, KeywordTagType.KeywordLayerTag -> TagHandlers(
            addKeyword = activeKeywordLayerDefStore.addKeyWord,
            removeKeyword = activeKeywordLayerDefStore.removeKeyWord,
            addOrUpdateFieldValue = currentActiveFieldValueStore.update,
            removeFieldValue = activeHeatmapLayerDefStore.removeFieldValue,
            addToSearchKeywords = activeObjectKeywordsStore.addToActiveSearchKeywords,
            addToSearchObjectTypes = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
            addToSearchReadOnlyKeywords = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            addToSearchFieldValues = activeSearchFieldValuesStore.add,
        )
    }
}

/* ---------------------------------------------------------------------------
 * Tailwind KeywordTag style
 * -------------------------------------------------------------------------*/
private const val COMMON = "rounded-full text-xs font-semibold px-2 py-1 m-1 gap-1 max-w-full whitespace-nowrap transition-colors border"

fun KeywordTagActionType.normalKeywordTagStyle(searchable: Boolean): String = when (this) {
    KeywordTagActionType.Add ->
        "$COMMON text-formationBlack bg-formationWhite border-formationBlack hover:bg-highlight hover:text-formationWhite hover:border-highlight focus:bg-highlight focus:text-formationWhite focus:border-highlight"

    KeywordTagActionType.AddFieldValue ->
        "$COMMON text-formationBlack bg-formationWhite border-formationBlack hover:bg-formationBlack hover:text-formationWhite hover:border-formationBlack focus:bg-formationBlack focus:text-formationWhite border-formationBlack"

    KeywordTagActionType.Default, KeywordTagActionType.Remove ->
        "$COMMON text-formationWhite bg-highlight border-highlight hover:text-highlight hover:bg-formationWhite focus:text-highlight focus:bg-formationWhite)"

    KeywordTagActionType.ReadOnly ->
        "$COMMON bg-gray-300 border-gray-300${if (searchable) " hover:bg-formationWhite focus:bg-formationWhite" else " cursor-default"}"

    KeywordTagActionType.RemoveReadOnly ->
        "$COMMON bg-gray-300 border-gray-300 hover:bg-formationWhite focus:bg-formationWhite"

    KeywordTagActionType.DefaultFieldValue, KeywordTagActionType.RemoveFieldValue ->
        "$COMMON text-formationWhite bg-formationBlack border-formationBlack hover:text-formationBlack hover:bg-formationWhite focus:text-formationBlack focus:bg-formationWhite"
}

/* ---------------------------------------------------------------------------
 * Normal KeywordTag component
 * -------------------------------------------------------------------------*/
fun RenderContext.keywordTag(
    tag: KeywordTag,
    keywordTagType: KeywordTagType,
    searchable: Boolean = false,
    condensed: Boolean = false,
): HtmlTag<HTMLElement> {
    val handlers = keywordTagType.handlers()
    val tagStyleClasses = tag.actionType.normalKeywordTagStyle(searchable)

    // Store for Modal that opens to change the value of the fieldValueTag (if it is one)
    val fieldValueModalToggleStore: Store<Boolean> = storeOf(false, Job())

    return twRowOfNoGap(tagStyleClasses) {
        button(
            id = "keywordTag-${Id.next()}",
        ) {
            // Modal that opens to change the value of the fieldValueTag (if it is one)
            twFieldValueModal(
                toggleShowModalStore = fieldValueModalToggleStore,
                tagType = keywordTagType,
            )

            div("flex items-center justify-center space-x-1 max-w-full") {
                if (tag.valueText != null) {
                    span("text-xs font-light ${if (condensed) "leading-tight" else ""}") {
                        +"${tag.fieldText} //"
                    }
                    span("text-xs font-bold ${if (condensed) "leading-tight" else ""}") {
                        +tag.valueText
                    }
                } else {
                    span("text-xs font-bold ${if (condensed) "leading-tight" else ""}") {
                        +tag.fieldText
                    }
                }

                when (tag.actionType) {
                    KeywordTagActionType.Add -> {
                        clicks handledBy {
                            handlers.addKeyword?.invoke(tag.fieldText)
                        }
                        twIconMedium(FormationUIIcons.Add)
                    }

                    KeywordTagActionType.Remove -> {
                        clicks handledBy {
                            handlers.removeKeyword?.invoke(tag.fieldText)
                        }
                        twIconMedium(FormationUIIcons.Close)
                    }

                    KeywordTagActionType.RemoveReadOnly -> {
                        /* Special logic for read‑only tags */
                        when (keywordTagType) {
                            KeywordTagType.SearchTag,
                            KeywordTagType.AnalyticsPathViewTag,
                            KeywordTagType.NestedObjectTag -> {
                                when (tag.readOnlyType) {
                                    ReadOnlyTags.ObjectType -> {
                                        clicks handledBy {
                                            handlers.removeObjectType?.invoke(tag.readOnlyStringValue)
                                        }
                                    }

                                    ReadOnlyTags.Assignee,
                                    ReadOnlyTags.Creator -> {
                                        clicks handledBy {
                                            handlers.removeReadOnlyKeyword?.invoke(tag)
                                        }
                                    }

                                    else -> {
                                        clicks handledBy {
                                            handlers.removeKeyword?.invoke(tag.fieldText)
                                        }
                                    }
                                }
                            }

                            else -> {
                                clicks handledBy {
                                    handlers.removeKeyword?.invoke(tag.fieldText)
                                }
                            }
                        }
                        twIconMedium(FormationUIIcons.Close)
                    }

                    KeywordTagActionType.AddFieldValue -> {
                        clicks handledBy {
                            handlers.addOrUpdateFieldValue?.invoke(tag.customFieldValue)
                            fieldValueModalToggleStore.update(true)
                        }
                        twIconMedium(FormationUIIcons.Add)
                    }

                    KeywordTagActionType.RemoveFieldValue -> {
                        clicks handledBy {
                            handlers.addOrUpdateFieldValue?.invoke(tag.customFieldValue)
                            fieldValueModalToggleStore.update(true)
                        }
                    }

                    else -> {}
                }
            }

            if (searchable) {
                when (tag.actionType) {
                    KeywordTagActionType.ReadOnly, KeywordTagActionType.RemoveReadOnly -> {
                        /* Special logic for read‑only tags */
                        when (tag.readOnlyType) {
                            ReadOnlyTags.ObjectType -> {
                                when (keywordTagType) {
                                    KeywordTagType.SearchTag,
                                    KeywordTagType.AnalyticsPathViewTag,
                                    KeywordTagType.NestedObjectTag -> {
                                        clicks handledBy {
                                            handlers.addObjectType?.invoke(tag.readOnlyStringValue)
                                        }
                                    }

                                    else -> {
                                        clicks handledBy {
                                            handlers.addToSearchObjectTypes?.invoke(tag.readOnlyStringValue)
                                        }
                                    }
                                }
                            }

                            ReadOnlyTags.Assignee, ReadOnlyTags.Creator -> {
                                when (keywordTagType) {
                                    KeywordTagType.SearchTag,
                                    KeywordTagType.AnalyticsPathViewTag,
                                    KeywordTagType.NestedObjectTag -> {
                                        clicks handledBy {
                                            handlers.addReadOnlyKeyword?.invoke(tag)
                                        }
                                    }

                                    else -> {
                                        clicks handledBy {
                                            handlers.addToSearchReadOnlyKeywords?.invoke(tag)
                                        }
                                    }
                                }
                            }

                            else -> {
                                when (keywordTagType) {
                                    KeywordTagType.AnalyticsPathViewTag, KeywordTagType.NestedObjectTag -> {
                                        clicks handledBy {
                                            handlers.addKeyword?.invoke(tag.fieldText)
                                        }

                                    }

                                    else -> {
                                        clicks handledBy {
                                            handlers.addToSearchKeywords?.invoke(tag.fieldText)
                                        }
                                    }
                                }
                            }
                        }
                    }

                    KeywordTagActionType.DefaultFieldValue, KeywordTagActionType.AddFieldValue, KeywordTagActionType.RemoveFieldValue -> {
                        when (keywordTagType) {
                            KeywordTagType.SearchTag,
                            KeywordTagType.AnalyticsPathViewTag -> {
                                clicks handledBy {
                                    handlers.addOrUpdateFieldValue?.invoke(tag.customFieldValue)
                                }
                            }

                            else -> {
                                clicks handledBy {
                                    handlers.addToSearchFieldValues?.invoke(tag.customFieldValue)
                                }
                            }
                        }

                    }

                    else -> {
                        clicks handledBy {
                            handlers.addToSearchKeywords?.invoke(tag.fieldText)
                        }
                    }
                }
            }
        }
        // button as dedicated click target with hover effect to remove fieldValue
        if (tag.actionType == KeywordTagActionType.RemoveFieldValue) {
            button("rounded-r-full hover:text-red-500") {
                twIconMedium(FormationUIIcons.Close)
                clicks handledBy {
                    handlers.removeFieldValue?.invoke(tag.customFieldValue)
                }
            }
        }
    }
}

/* ---------------------------------------------------------------------------
 * TinyKeywordTag data + handler helpers
 * -------------------------------------------------------------------------*/

data class TinyTagHandlers(
    val addKeywordHandler: SimpleHandler<String>? = null,
    val addFieldValueHandler: SimpleHandler<FieldValue?>? = null,
    val addReadOnlyKeywordHandler: SimpleHandler<KeywordTag>? = null,
    val addObjectTypeHandler: SimpleHandler<String?>? = null,
)

fun KeywordTagType.tinyTagHandlers(): TinyTagHandlers = withKoin {
    val activeObjectKeywordsStore: ActiveObjectKeywordsStore = get()
    val activeSearchFieldValuesStore: ActiveSearchFieldValuesStore = get()
    val activeArchetypeFieldValues: ActiveArchetypeSearchFieldValuesStore = get()
    val activeArchetypeKeywords: ActiveArchetypeSearchKeywordsStore = get()
    val activeHistoryKeywords: ActiveHistoryPathSearchKeywordsStore = get()
    val activeHistoryFieldValues: ActiveHistoryPathSearchFieldValuesStore = get()
    val activeHistoryObjectTypes: ActiveHistoryPathSearchObjectTypesStore = get()
    val activeHistoryReadOnly: ActiveHistoryPathSearchReadOnlyKeywordsStore = get()
    val activeNestedKeywords: ActiveNestedSearchKeywordStore = get()
    val activeNestedFieldValues: ActiveNestedSearchFieldValuesStore = get()

    return when (this@tinyTagHandlers) {
        KeywordTagType.ArchetypeTag -> TinyTagHandlers(
            addKeywordHandler = activeArchetypeKeywords.add,
            addFieldValueHandler = activeArchetypeFieldValues.add,
            addReadOnlyKeywordHandler = null,
            addObjectTypeHandler = null,
        )

        KeywordTagType.AnalyticsPathViewTag -> TinyTagHandlers(
            addKeywordHandler = activeHistoryKeywords.add,
            addFieldValueHandler = activeHistoryFieldValues.add,
            addReadOnlyKeywordHandler = activeHistoryReadOnly.add,
            addObjectTypeHandler = activeHistoryObjectTypes.add,
        )

        KeywordTagType.NestedObjectTag -> TinyTagHandlers(
            addKeywordHandler = activeNestedKeywords.add,
            addFieldValueHandler = activeNestedFieldValues.add,
            addReadOnlyKeywordHandler = activeObjectKeywordsStore.addToActiveNestedObjectReadOnlyTags,
            addObjectTypeHandler = activeObjectKeywordsStore.addToActiveNestedObjectTypesWithoutRouting,
        )

        else -> TinyTagHandlers(
            addKeywordHandler = activeObjectKeywordsStore.addToActiveSearchKeywords,
            addFieldValueHandler = activeSearchFieldValuesStore.add,
            addReadOnlyKeywordHandler = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags,
            addObjectTypeHandler = activeObjectKeywordsStore.addToActiveSearchObjectTypes,
        )
    }
}

/* ---------------------------------------------------------------------------
 * TinyKeywordTag style
 * -------------------------------------------------------------------------*/
private const val COMMON_TINY_TAG = "rounded-full m-1 max-w-full whitespace-nowrap transition-colors border"

fun KeywordTagActionType.tinyTagStyle(searchable: Boolean): String = when (this) {
    KeywordTagActionType.ReadOnly ->
        "$COMMON_TINY_TAG bg-gray-300 border-gray-300${if (searchable) " hover:bg-formationWhite focus:bg-formationWhite" else " cursor-default"}"

    KeywordTagActionType.DefaultFieldValue ->
        "$COMMON_TINY_TAG text-formationWhite bg-formationBlack border-formationBlack hover:text-formationBlack hover:bg-formationWhite focus:text-formationBlack focus:bg-formationWhite"

    else -> "$COMMON_TINY_TAG text-formationWhite bg-highlight border-highlight hover:text-highlight hover:bg-formationWhite focus:text-highlight focus:bg-formationWhite)"
}

/* ---------------------------------------------------------------------------
 * Tiny Keyword component
 * -------------------------------------------------------------------------*/

fun RenderContext.tinyKeyWordTag(
    tag: KeywordTag,
    searchable: Boolean = false,
    tagType: KeywordTagType
): HtmlTag<HTMLButtonElement> {

    val tagStyleClasses = tag.actionType.tinyTagStyle(searchable)
    val handlers = tagType.tinyTagHandlers()

    return button(
        tagStyleClasses,
        id = "keywordTag-${Id.next()}",
    ) {
        div(
            "flex flex-row w-max h-max items-center justify-center " +
                "rounded-full px-2",
        ) {
            // Text of the tag
            if (tag.valueText != null) {
                twRowOfNoGap("gap-1") {
                    p("font-light text-[9px]") {
                        +"${tag.fieldText} //"
                    }
                    p("font-bold text-[9px]") {
                        +tag.valueText
                    }
                }
            } else {
                p("font-bold text-[9px]") {
                    +tag.fieldText
                }
            }
            if (searchable) {
                // Click handlers depending on the KeywordTagActionType
                when (tag.actionType) {
                    KeywordTagActionType.ReadOnly -> {
                        // Click handlers of read only tags, depending on the readOnlyType
                        when (tag.readOnlyType) {
                            ReadOnlyTags.ObjectType -> {
                                handlers.addObjectTypeHandler?.let { handler ->
                                    clicks.map {
                                        it.stopPropagation()
                                        tag.readOnlyStringValue
                                    } handledBy handler
                                }
                            }

                            ReadOnlyTags.Assignee, ReadOnlyTags.Creator -> {
                                handlers.addReadOnlyKeywordHandler?.let { handler ->
                                    clicks.map {
                                        it.stopPropagation()
                                        tag
                                    } handledBy handler
                                }
                            }

                            else -> {
                                handlers.addKeywordHandler?.let { handler ->
                                    clicks.map {
                                        it.stopPropagation()
                                        tag.fieldText
                                    } handledBy handler
                                }
                            }
                        }
                    }

                    KeywordTagActionType.DefaultFieldValue -> {
                        handlers.addFieldValueHandler?.let { handler ->
                            clicks.map {
                                it.stopPropagation()
                                tag.customFieldValue
                            } handledBy handler
                        }
                    }

                    else -> {
                        handlers.addKeywordHandler?.let { handler ->
                            clicks.map {
                                it.stopPropagation()
                                tag.fieldText
                            } handledBy handler
                        }
                    }
                }
            }
        }
    }
}

fun RenderContext.keywordTagList(
    keywords: Flow<List<KeywordTag>>,
    keywordTagType: KeywordTagType,
    searchable: Boolean = false,
    tagSize: KeywordTagSize = KeywordTagSize.Normal,
    insertAfterBlock: (HtmlTag<HTMLDivElement>.() -> Unit)? = null
) {
    keywords.render { keywordList ->
        if (keywordList.isNotEmpty()) {
            div("flex w-full h-max flex-wrap") {
                keywordList.sortKeywordTags().forEach { keywordTag ->
                    when (tagSize) {
                        KeywordTagSize.Tiny -> tinyKeyWordTag(
                            tag = keywordTag,
                            searchable = searchable,
                            tagType = keywordTagType,
                        )

                        KeywordTagSize.Normal -> keywordTag(
                            tag = keywordTag,
                            searchable = searchable,
                            keywordTagType = keywordTagType,
                        )
                    }
                }
                insertAfterBlock?.invoke(this)
            }
        }
    }
}

//fun RenderContext.keywordTagOld(
//    tag: KeywordTag,
//    keywordTagType: KeywordTagType,
//    optRemoveHandler: SimpleHandler<String>? = null,
//    searchable: Boolean = false,
//    condensed: Boolean = false
//): HtmlTag<HTMLButtonElement> {
//    val currentActiveFieldValueStore: CurrentActiveFieldValueStore by koinCtx.inject()
//
//    val activeObjectKeywordsStore: ActiveObjectKeywordsStore by koinCtx.inject()
//
//    val activeSearchFieldValuesStore: ActiveSearchFieldValuesStore by koinCtx.inject()
//    val activeSearchKeywordsStore: ActiveSearchKeywordsStore by koinCtx.inject()
//
//    val activeArchetypeSearchFieldValuesStore: ActiveArchetypeSearchFieldValuesStore by koinCtx.inject()
//    val activeArchetypeSearchKeywordsStore: ActiveArchetypeSearchKeywordsStore by koinCtx.inject()
//
//    val activeKeywordLayerDefinitionStore: ActiveKeywordLayerDefinitionStore by koinCtx.inject()
//    val activeHeatmapLayerDefinitionStore: ActiveHeatmapLayerDefinitionStore by koinCtx.inject()
//
//    val activeHistoryPathSearchKeywordsStore: ActiveHistoryPathSearchKeywordsStore by koinCtx.inject()
//    val activeHistoryPathSearchObjectTypesStore: ActiveHistoryPathSearchObjectTypesStore by koinCtx.inject()
//    val activeHistoryPathSearchReadOnlyKeywordsStore: ActiveHistoryPathSearchReadOnlyKeywordsStore by koinCtx.inject()
//    val activeHistoryPathSearchFieldValuesStore: ActiveHistoryPathSearchFieldValuesStore by koinCtx.inject()
//
//    val activeNestedSearchKeywordStore: ActiveNestedSearchKeywordStore by koinCtx.inject()
//    val activeNestedSearchFieldValuesStore: ActiveNestedSearchFieldValuesStore by koinCtx.inject()
//
//    val addHandler: SimpleHandler<String>
//    val removeHandler: SimpleHandler<String>
//    val addFieldValueHandler: SimpleHandler<FieldValue?>
//    val removeFieldValueHandler: SimpleHandler<FieldValue?>
//    val addObjectTypeHandler: SimpleHandler<String?>?
//    val removeObjectTypeHandler: SimpleHandler<String?>?
//    val addReadOnlyKeywordHandler: SimpleHandler<KeywordTag>?
//    val removeReadOnlyKeywordHandler: SimpleHandler<KeywordTag>?
//
//    // Store for Modal that opens to change the value of the fieldValueTag (if it is one)
//    val fieldValueModalToggleStore: Store<Boolean> = storeOf(false, Job())
//
//    // Define handlers for different keywordTagTypes
//    when (keywordTagType) {
//        KeywordTagType.ObjectTag -> {
//            addHandler = activeObjectKeywordsStore.directEditAddKeywordToActiveObject
//            removeHandler = activeObjectKeywordsStore.directEditRemoveKeywordFromActiveObject
//            addFieldValueHandler = SimpleHandler { data, _ ->
//                data handledBy { fieldValue ->
//                    currentActiveFieldValueStore.update(fieldValue)
//                    fieldValueModalToggleStore.update(true)
//                }
//            }
//            removeFieldValueHandler = activeObjectKeywordsStore.directEditRemoveFieldValueFromObject
//            addObjectTypeHandler = null
//            removeObjectTypeHandler = null
//            addReadOnlyKeywordHandler = null
//            removeReadOnlyKeywordHandler = null
//        }
//
//        KeywordTagType.SearchTag -> {
//            addHandler = activeSearchKeywordsStore.add
//            removeHandler = activeSearchKeywordsStore.remove
//            addFieldValueHandler = activeSearchFieldValuesStore.add
//            removeFieldValueHandler = activeSearchFieldValuesStore.remove
//            addObjectTypeHandler = activeObjectKeywordsStore.addToActiveSearchObjectTypes
//            removeObjectTypeHandler = activeObjectKeywordsStore.removeFromActiveSearchObjectTypes
//            addReadOnlyKeywordHandler = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags
//            removeReadOnlyKeywordHandler = activeObjectKeywordsStore.removeFromActiveSearchReadOnlyTags
//        }
//
//        KeywordTagType.AnalyticsPathViewTag -> {
//            addHandler = activeHistoryPathSearchKeywordsStore.add
//            removeHandler = activeHistoryPathSearchKeywordsStore.remove
//            addFieldValueHandler = activeHistoryPathSearchFieldValuesStore.add
//            removeFieldValueHandler = activeHistoryPathSearchFieldValuesStore.remove
//            addObjectTypeHandler = activeHistoryPathSearchObjectTypesStore.add
//            removeObjectTypeHandler = activeHistoryPathSearchObjectTypesStore.remove
//            addReadOnlyKeywordHandler = activeHistoryPathSearchReadOnlyKeywordsStore.add
//            removeReadOnlyKeywordHandler = activeHistoryPathSearchReadOnlyKeywordsStore.remove
//        }
//
//        KeywordTagType.ArchetypeTag -> {
//            addHandler = activeArchetypeSearchKeywordsStore.add
//            removeHandler = activeArchetypeSearchKeywordsStore.remove
//            addFieldValueHandler = activeArchetypeSearchFieldValuesStore.add
//            removeFieldValueHandler = activeArchetypeSearchFieldValuesStore.remove
//            addObjectTypeHandler = null
//            removeObjectTypeHandler = null
//            addReadOnlyKeywordHandler = null
//            removeReadOnlyKeywordHandler = null
//        }
//
//        KeywordTagType.NestedObjectTag -> {
//            addHandler = activeNestedSearchKeywordStore.add
//            removeHandler = activeNestedSearchKeywordStore.remove
//            addFieldValueHandler = activeNestedSearchFieldValuesStore.add
//            removeFieldValueHandler = activeNestedSearchFieldValuesStore.remove
//            addObjectTypeHandler = activeObjectKeywordsStore.addToActiveNestedObjectTypesWithoutRouting
//            removeObjectTypeHandler = activeObjectKeywordsStore.removeFromActiveNestedObjectTypes
//            addReadOnlyKeywordHandler = activeObjectKeywordsStore.addToActiveNestedObjectReadOnlyTags
//            removeReadOnlyKeywordHandler = activeObjectKeywordsStore.removeFromActiveNestedObjectReadOnlyTags
//        }
//
//        KeywordTagType.HeatmapLayerTag -> {
//            addHandler = activeKeywordLayerDefinitionStore.addKeyWord
//            removeHandler = activeKeywordLayerDefinitionStore.removeKeyWord
//            addFieldValueHandler = activeHeatmapLayerDefinitionStore.addFieldValue
//            removeFieldValueHandler = activeHeatmapLayerDefinitionStore.removeFieldValue
//            addObjectTypeHandler = null
//            removeObjectTypeHandler = null
//            addReadOnlyKeywordHandler = null
//            removeReadOnlyKeywordHandler = null
//        }
//
//        KeywordTagType.KeywordLayerTag -> {
//            addHandler = activeKeywordLayerDefinitionStore.addKeyWord
//            removeHandler = activeKeywordLayerDefinitionStore.removeKeyWord
//            addFieldValueHandler = activeKeywordLayerDefinitionStore.addFieldValue
//            removeFieldValueHandler = activeKeywordLayerDefinitionStore.removeFieldValue
//            addObjectTypeHandler = null
//            removeObjectTypeHandler = null
//            addReadOnlyKeywordHandler = null
//            removeReadOnlyKeywordHandler = null
//        }
//    }
//
//    return button(
//        {
//            radius(formationStyles.tagRadius)
//            margin { tiny }
//            minWidth { maxContent }
//            maxWidth { full }
//            // Set styling depending on the KeywordTagActionType of the tag
//            when (tag.actionType) {
//                KeywordTagActionType.Add -> {
//                    color { primary.main }
//                    background {
//                        color { secondary.main }
//                    }
//                    border {
//                        color { primary.main }
//                        width(formationStyles.borderWidth)
//                    }
//                    hover {
//                        color { secondary.main }
//                        background {
//                            color { FormationColors.MarkerYou.color }
//                        }
//                        border {
//                            color { FormationColors.MarkerYou.color }
//                            width(formationStyles.borderWidth)
//                        }
//                    }
//                    focus {
//                        color { secondary.main }
//                        background {
//                            color { FormationColors.MarkerYou.color }
//                        }
//                        border {
//                            color { FormationColors.MarkerYou.color }
//                            width(formationStyles.borderWidth)
//                        }
//                    }
//                }
//
//                KeywordTagActionType.ReadOnly -> {
//                    background {
//                        color { FormationColors.GrayPrivate.color }
//                    }
//                    border {
//                        color { FormationColors.GrayPrivate.color }
//                        width(formationStyles.borderWidth)
//                    }
//                    if (searchable) {
//                        hover {
//                            background {
//                                color { secondary.main }
//                            }
//                            border {
//                                width(formationStyles.borderWidth)
//                                color { FormationColors.GrayPrivate.color }
//                            }
//                        }
//                        focus {
//                            background {
//                                color { secondary.main }
//                            }
//                            border {
//                                width(formationStyles.borderWidth)
//                                color { FormationColors.GrayPrivate.color }
//                            }
//                        }
//                    } else {
//                        css("cursor:default;")
//                    }
//                }
//
//                KeywordTagActionType.RemoveReadOnly -> {
//                    background {
//                        color { FormationColors.GrayPrivate.color }
//                    }
//                    border {
//                        color { FormationColors.GrayPrivate.color }
//                        width(formationStyles.borderWidth)
//                    }
//                    hover {
//                        background {
//                            color { secondary.main }
//                        }
//                        border {
//                            width(formationStyles.borderWidth)
//                            color { FormationColors.GrayPrivate.color }
//                        }
//                    }
//                    focus {
//                        background {
//                            color { secondary.main }
//                        }
//                        border {
//                            width(formationStyles.borderWidth)
//                            color { FormationColors.GrayPrivate.color }
//                        }
//                    }
//                }
//
//                KeywordTagActionType.AddFieldValue -> {
//                    color { primary.main }
//                    background {
//                        color { secondary.main }
//                    }
//                    border {
//                        color { primary.main }
//                        width(formationStyles.borderWidth)
//                    }
//                    hover {
//                        color { secondary.main }
//                        background {
//                            color { primary.main }
//                        }
//                        border {
//                            color { primary.main }
//                            width(formationStyles.borderWidth)
//                        }
//                    }
//                    focus {
//                        color { secondary.main }
//                        background {
//                            color { primary.main }
//                        }
//                        border {
//                            color { primary.main }
//                            width(formationStyles.borderWidth)
//                        }
//                    }
//                }
//
//                KeywordTagActionType.DefaultFieldValue, KeywordTagActionType.RemoveFieldValue -> {
//                    color { secondary.main }
//                    background {
//                        color { primary.main }
//                    }
//                    border {
//                        color { primary.main }
//                        width(formationStyles.borderWidth)
//                    }
//                    hover {
//                        color { primary.main }
//                        background {
//                            color { secondary.main }
//                        }
//                    }
//                    focus {
//                        color { primary.main }
//                        background {
//                            color { secondary.main }
//                        }
//                    }
//                }
//
//                //KeywordTagType.Default, KeywordTagType.Remove
//                else -> {
//                    color { secondary.main }
//                    background {
//                        color { FormationColors.MarkerYou.color }
//                    }
//                    border {
//                        color { FormationColors.MarkerYou.color }
//                        width(formationStyles.borderWidth)
//                    }
//                    hover {
//                        color { FormationColors.MarkerYou.color }
//                        background {
//                            color { secondary.main }
//                        }
//                        border {
//                            width(formationStyles.borderWidth)
//                            color { FormationColors.MarkerYou.color }
//                        }
//                    }
//                    focus {
//                        color { FormationColors.MarkerYou.color }
//                        background {
//                            color { secondary.main }
//                        }
//                        border {
//                            width(formationStyles.borderWidth)
//                            color { FormationColors.MarkerYou.color }
//                        }
//                    }
//                }
//            }
//        },
//        id = "keywordTag-${Id.next()}",
//    ) {
//        // Modal that opens to change the value of the fieldValueTag (if it is one)
//        twFieldValueModal(
//            toggleShowModalStore = fieldValueModalToggleStore,
//            tagType = keywordTagType,
//        )
//
//        flexBox(
//            {
//                direction { row }
//                maxWidth { full }
//                height { maxContent }
//                alignItems { center }
//                justifyContent { center }
//                radius(formationStyles.tagRadius)
//                paddings {
//                    vertical { tiny }
//                    horizontal { smaller }
//                }
//            },
//        ) {
//            // Text inside Tag
//            if (tag.valueText != null) {
//                lineUp {
//                    spacing { tiny }
//                    items {
//                        span(
//                            {
//                                fontWeight { lighter }
//                                fontSize { smaller }
//                                if (condensed) lineHeight { "1.2" }
//                                when (tag.actionType) {
//                                    // when tag has icon
//                                    KeywordTagActionType.Add,
//                                    KeywordTagActionType.Remove,
//                                    KeywordTagActionType.RemoveReadOnly,
//                                    KeywordTagActionType.AddFieldValue,
//                                    KeywordTagActionType.RemoveFieldValue -> margins { left { tiny } }
//
//                                    // KeywordTagActionType.Default,
//                                    // KeywordTagActionType.ReadOnly,
//                                    // KeywordTagActionType.DefaultFieldValue
//                                    else -> {}
//                                }
//                            },
//                        ) { +"${tag.fieldText} //" }
//                        span(
//                            {
//                                fontWeight { bold }
//                                fontSize { smaller }
//                                if (condensed) lineHeight { "1.2" }
//                            },
//                        ) { +tag.valueText }
//                    }
//                }
//            } else {
//                span(
//                    {
//                        fontWeight { bold }
//                        fontSize { smaller }
//                        if (condensed) lineHeight { "1.2" }
//                        when (tag.actionType) {
//                            KeywordTagActionType.Add,
//                            KeywordTagActionType.Remove,
//                            KeywordTagActionType.RemoveReadOnly,
//                            KeywordTagActionType.AddFieldValue,
//                            KeywordTagActionType.RemoveFieldValue -> margins { left { tiny } }
//
//                            else -> {}
//                        }
//                    },
//                ) { +tag.fieldText }
//            }
//            // Icon inside Tag (+, x, or no icon)
//            // and click handlers on the icon, depending on the KeywordTagActionType
//            when (tag.actionType) {
//                KeywordTagActionType.Add -> {
//                    button(
//                        {
//                            margins { left { tiny } }
//                            if (searchable) {
//                                hover {
//                                    color { makeRGBA(secondary.main, 0.5) }
//                                }
//                            }
//                        },
//                    ) {
//                        attr("tabindex", "-1")
//                        icon { fromTheme { FormationUIIcons.Add.icon } }
//                        clicks.map { tag.fieldText } handledBy addHandler
//                    }
//                }
//
//                KeywordTagActionType.Remove -> {
//                    button(
//                        {
//                            margins { left { tiny } }
//                            if (searchable) {
//                                hover {
//                                    color { makeRGBA(FormationColors.MarkerYou.color, 0.5) }
//                                }
//                            }
//                        },
//                    ) {
//                        attr("tabindex", "-1")
//                        icon { fromTheme { close } }
//                        optRemoveHandler?.let {
//                            clicks.map { tag.fieldText } handledBy it
//                        } ?: (clicks.map { tag.fieldText } handledBy removeHandler)
//                    }
//                }
//
//                KeywordTagActionType.RemoveReadOnly -> {
//                    button(
//                        {
//                            margins { left { tiny } }
//                            if (searchable) {
//                                hover {
//                                    color { makeRGBA(FormationColors.GrayPrivate.color, 0.7) }
//                                }
//                            }
//                        },
//                    ) {
//                        attr("tabindex", "-1")
//                        icon { fromTheme { close } }
//
//                        when (keywordTagType) {
//                            KeywordTagType.SearchTag,
//                            KeywordTagType.AnalyticsPathViewTag,
//                            KeywordTagType.NestedObjectTag -> {
//                                when (tag.readOnlyType) {
//                                    ReadOnlyTags.ObjectType -> {
//                                        removeObjectTypeHandler?.let { handler ->
//                                            clicks.map { tag.readOnlyStringValue } handledBy handler
//                                        }
//                                    }
//
//                                    ReadOnlyTags.Assignee,
//                                    ReadOnlyTags.Creator -> {
//                                        removeReadOnlyKeywordHandler?.let { handler ->
//                                            clicks.map { tag } handledBy handler
//                                        }
//                                    }
//
//                                    else -> {
//                                        optRemoveHandler?.let { handler ->
//                                            clicks.map { tag.fieldText } handledBy handler
//                                        } ?: (clicks.map { tag.fieldText } handledBy removeHandler)
//                                    }
//                                }
//                            }
//
//                            else -> {
//                                optRemoveHandler?.let { handler ->
//                                    clicks.map { tag.fieldText } handledBy handler
//                                } ?: (clicks.map { tag.fieldText } handledBy removeHandler)
//                            }
//                        }
//                    }
//                }
//
//                KeywordTagActionType.AddFieldValue -> {
//                    button(
//                        {
//                            margins { left { tiny } }
//                            if (searchable) {
//                                hover {
//                                    color { makeRGBA(secondary.main, 0.5) }
//                                }
//                            }
//                        },
//                    ) {
//                        attr("tabindex", "-1")
//                        icon { fromTheme { FormationUIIcons.Add.icon } }
//
//                        clicks.map { tag.customFieldValue } handledBy addFieldValueHandler
//                    }
//                }
//
//                KeywordTagActionType.RemoveFieldValue -> {
//                    button(
//                        {
//                            margins { left { tiny } }
//                            if (searchable) {
//                                hover {
//                                    color { makeRGBA(FormationColors.GrayPrivate.color, 0.7) }
//                                }
//                            }
//                        },
//                    ) {
//                        attr("tabindex", "-1")
//                        icon { fromTheme { close } }
//                        clicks.map { tag.customFieldValue } handledBy removeFieldValueHandler
//                    }
//                }
//
//                else -> {} // KeyWordTagType Default, ReadOnly
//            }
//        }
//        // Click Handlers on the tag, depending on the KeywordTagActionType
//        if (searchable) {
//            when (tag.actionType) {
//                KeywordTagActionType.ReadOnly, KeywordTagActionType.RemoveReadOnly -> {
//                    when (tag.readOnlyType) {
//                        ReadOnlyTags.ObjectType -> {
//                            when (keywordTagType) {
//                                KeywordTagType.SearchTag,
//                                KeywordTagType.AnalyticsPathViewTag,
//                                KeywordTagType.NestedObjectTag -> {
//                                    addObjectTypeHandler?.let { handler ->
//                                        clicks.map { tag.readOnlyStringValue } handledBy handler
//                                    }
//                                }
//
//                                else -> clicks.map { tag.readOnlyStringValue } handledBy activeObjectKeywordsStore.addToActiveSearchObjectTypes
//                            }
//                        }
//
//                        ReadOnlyTags.Assignee, ReadOnlyTags.Creator -> {
//                            when (keywordTagType) {
//                                KeywordTagType.SearchTag,
//                                KeywordTagType.AnalyticsPathViewTag,
//                                KeywordTagType.NestedObjectTag -> {
//                                    addReadOnlyKeywordHandler?.let { handler ->
//                                        clicks.map { tag } handledBy handler
//                                    }
//                                }
//
//                                else -> clicks.map { tag } handledBy activeObjectKeywordsStore.addToActiveSearchReadOnlyTags
//                            }
//                        }
//
//                        else -> {
//                            when (keywordTagType) {
//                                KeywordTagType.AnalyticsPathViewTag, KeywordTagType.NestedObjectTag -> {
//                                    clicks.map { tag.fieldText } handledBy addHandler
//                                }
//
//                                else -> clicks.map { tag.fieldText } handledBy activeObjectKeywordsStore.addToActiveSearchKeywords
//                            }
//                        }
//                    }
//                }
//
//                KeywordTagActionType.DefaultFieldValue, KeywordTagActionType.AddFieldValue, KeywordTagActionType.RemoveFieldValue -> {
//                    when (keywordTagType) {
//                        KeywordTagType.SearchTag,
//                        KeywordTagType.AnalyticsPathViewTag -> {
//                            clicks.map { tag.customFieldValue } handledBy addFieldValueHandler
//                        }
//
//                        else -> {
//                            clicks.map { tag.customFieldValue } handledBy activeSearchFieldValuesStore.add
//                        }
//                    }
//                }
//
//                else -> {
//                    clicks.map { tag.fieldText } handledBy activeObjectKeywordsStore.addToActiveSearchKeywords
//                }
//            }
//        } else {
//            when (tag.actionType) {
//                KeywordTagActionType.Add -> {
//                    clicks.map { tag.fieldText } handledBy addHandler
//                }
//
//                KeywordTagActionType.Remove -> {
//                    optRemoveHandler?.let {
//                        clicks.map { tag.fieldText } handledBy it
//                    } ?: (clicks.map { tag.fieldText } handledBy removeHandler)
//                }
//
//                KeywordTagActionType.RemoveReadOnly -> {
//                    when (keywordTagType) {
//                        KeywordTagType.SearchTag,
//                        KeywordTagType.AnalyticsPathViewTag,
//                        KeywordTagType.NestedObjectTag -> {
//                            when (tag.readOnlyType) {
//                                ReadOnlyTags.ObjectType -> {
//                                    removeObjectTypeHandler?.let { handler ->
//                                        clicks.map { tag.readOnlyStringValue } handledBy handler
//                                    }
//                                }
//
//                                ReadOnlyTags.Assignee, ReadOnlyTags.Creator -> {
//                                    removeReadOnlyKeywordHandler?.let { handler ->
//                                        clicks.map { tag } handledBy handler
//                                    }
//                                }
//
//                                else -> optRemoveHandler?.let {
//                                    clicks.map { tag.fieldText } handledBy it
//                                } ?: (clicks.map { tag.fieldText } handledBy removeHandler)
//                            }
//                        }
//
//                        else -> {
//                            optRemoveHandler?.let {
//                                clicks.map { tag.fieldText } handledBy it
//                            } ?: (clicks.map { tag.fieldText } handledBy removeHandler)
//                        }
//                    }
//                }
//
//                KeywordTagActionType.DefaultFieldValue -> {
//                    clicks.map { tag.customFieldValue } handledBy addFieldValueHandler
//                }
//                // Add or change category tag
//                KeywordTagActionType.AddFieldValue, KeywordTagActionType.RemoveFieldValue -> {
//                    clicks.map { tag.customFieldValue } handledBy addFieldValueHandler
//                }
//
//                else -> {} // KeyWordTagType Default, ReadOnly
//            }
//        }
//    }
//}


//fun RenderContext.tinyKeyWordTagOld(
//    tag: KeywordTag,
//    searchable: Boolean = false,
//    tagType: KeywordTagType
//): HtmlTag<HTMLButtonElement> {
//    val activeObjectKeywordsStore: ActiveObjectKeywordsStore by koinCtx.inject()
//    val activeArchetypeSearchKeywordsStore: ActiveArchetypeSearchKeywordsStore by koinCtx.inject()
//    val activeNestedSearchKeywordStore: ActiveNestedSearchKeywordStore by koinCtx.inject()
//    val activeSearchFieldValuesStore: ActiveSearchFieldValuesStore by koinCtx.inject()
//    val activeArchetypeSearchFieldValuesStore: ActiveArchetypeSearchFieldValuesStore by koinCtx.inject()
//    val activeNestedSearchFieldValuesStore: ActiveNestedSearchFieldValuesStore by koinCtx.inject()
//
//    val activeHistoryPathSearchKeywordsStore: ActiveHistoryPathSearchKeywordsStore by koinCtx.inject()
//    val activeHistoryPathSearchObjectTypesStore: ActiveHistoryPathSearchObjectTypesStore by koinCtx.inject()
//    val activeHistoryPathSearchReadOnlyKeywordsStore: ActiveHistoryPathSearchReadOnlyKeywordsStore by koinCtx.inject()
//    val activeHistoryPathSearchFieldValuesStore: ActiveHistoryPathSearchFieldValuesStore by koinCtx.inject()
//
//    val addKeywordHandler: SimpleHandler<String>
//    val addFieldValueHandler: SimpleHandler<FieldValue?>
//    val addReadOnlyKeywordHandler: SimpleHandler<KeywordTag>?
//    val addObjectTypeHandler: SimpleHandler<String?>?
//
//    // Define handlers for different keywordTagTypes
//    when (tagType) {
//        KeywordTagType.ArchetypeTag -> {
//            addKeywordHandler = activeArchetypeSearchKeywordsStore.add
//            addFieldValueHandler = activeArchetypeSearchFieldValuesStore.add
//            addReadOnlyKeywordHandler = null
//            addObjectTypeHandler = null
//        }
//
//        KeywordTagType.AnalyticsPathViewTag -> {
//            addKeywordHandler = activeHistoryPathSearchKeywordsStore.add
//            addFieldValueHandler = activeHistoryPathSearchFieldValuesStore.add
//            addReadOnlyKeywordHandler = activeHistoryPathSearchReadOnlyKeywordsStore.add
//            addObjectTypeHandler = activeHistoryPathSearchObjectTypesStore.add
//        }
//
//        KeywordTagType.NestedObjectTag -> {
//            addKeywordHandler = activeNestedSearchKeywordStore.add
//            addFieldValueHandler = activeNestedSearchFieldValuesStore.add
//            addReadOnlyKeywordHandler = activeObjectKeywordsStore.addToActiveNestedObjectReadOnlyTags
//            addObjectTypeHandler = activeObjectKeywordsStore.addToActiveNestedObjectTypesWithoutRouting
//        }
//
//        else -> {
//            addKeywordHandler = activeObjectKeywordsStore.addToActiveSearchKeywords
//            addFieldValueHandler = activeSearchFieldValuesStore.add
//            addReadOnlyKeywordHandler = activeObjectKeywordsStore.addToActiveSearchReadOnlyTags
//            addObjectTypeHandler = activeObjectKeywordsStore.addToActiveSearchObjectTypes
//        }
//    }
//
//
//
//    return button(
//        {
//            radius(formationStyles.tagRadius)
//            margin { tiny }
//            // Set styling depending on the KeywordTagActionType
//            when (tag.actionType) {
//                KeywordTagActionType.ReadOnly -> {
//                    background {
//                        color { FormationColors.GrayPrivate.color }
//                    }
//                    border {
//                        color { FormationColors.GrayPrivate.color }
//                        width(formationStyles.borderWidth)
//                    }
//                    if (searchable) {
//                        hover {
//                            background {
//                                color { secondary.main }
//                            }
//                        }
//                        focus {
//                            background {
//                                color { secondary.main }
//                            }
//                        }
//                    } else {
//                        css("cursor:default;")
//                    }
//                }
//
//                KeywordTagActionType.DefaultFieldValue -> {
//                    color { secondary.main }
//                    background {
//                        color { primary.main }
//                    }
//                    border {
//                        color { primary.main }
//                        width(formationStyles.borderWidth)
//                    }
//                    hover {
//                        color { primary.main }
//                        background {
//                            color { secondary.main }
//                        }
//                    }
//                    focus {
//                        color { primary.main }
//                        background {
//                            color { secondary.main }
//                        }
//                    }
//                }
//
//                else -> {
//                    color { secondary.main }
//                    background {
//                        color { FormationColors.MarkerYou.color }
//                    }
//                    border {
//                        color { FormationColors.MarkerYou.color }
//                        width(formationStyles.borderWidth)
//                    }
//                    hover {
//                        color { FormationColors.MarkerYou.color }
//                        background {
//                            color { secondary.main }
//                        }
//                    }
//                    focus {
//                        color { FormationColors.MarkerYou.color }
//                        background {
//                            color { secondary.main }
//                        }
//                    }
//                }
//            }
//        },
//        id = "keywordTag-${Id.next()}",
//    ) {
//        flexBox(
//            {
//                direction { row }
//                width { maxContent }
//                height { maxContent }
//                alignItems { center }
//                justifyContent { center }
//                radius(formationStyles.tagRadius)
//                paddings {
//                    horizontal { smaller }
//                }
//            },
//        ) {
//            // Text of the tag
//            if (tag.valueText != null) {
//                lineUp {
//                    spacing { tiny }
//                    items {
//                        span(
//                            {
//                                fontWeight { bold }
//                                fontSize { "9px" }
//                            },
//                        ) { +"${tag.fieldText} //" }
//                        span(
//                            {
//                                fontWeight { lighter }
//                                fontSize { "9px" }
//                            },
//                        ) { +tag.valueText }
//                    }
//                }
//            } else {
//                span(
//                    {
//                        fontWeight { bold }
//                        fontSize { "9px" }
//                    },
//                ) { +tag.fieldText }
//            }
//            if (searchable) {
//                // Click handlers depending on the KeywordTagActionType
//                when (tag.actionType) {
//                    KeywordTagActionType.ReadOnly -> {
//                        // Click handlers of read only tags, depending on the readOnlyType
//                        when (tag.readOnlyType) {
//                            ReadOnlyTags.ObjectType -> {
//                                addObjectTypeHandler?.let { handler ->
//                                    clicks.map {
//                                        it.stopPropagation()
//                                        tag.readOnlyStringValue
//                                    } handledBy handler
//                                }
//                            }
//
//                            ReadOnlyTags.Assignee, ReadOnlyTags.Creator -> {
//                                addReadOnlyKeywordHandler?.let { handler ->
//                                    clicks.map {
//                                        it.stopPropagation()
//                                        tag
//                                    } handledBy handler
//                                }
//                            }
//
//                            else -> clicks.map {
//                                it.stopPropagation()
//                                tag.fieldText
//                            } handledBy addKeywordHandler
//                        }
//                    }
//
//                    KeywordTagActionType.DefaultFieldValue -> {
//                        clicks.map {
//                            it.stopPropagation()
//                            tag.customFieldValue
//                        } handledBy addFieldValueHandler
//                    }
//
//                    else -> {
//                        clicks.map {
//                            it.stopPropagation()
//                            tag.fieldText
//                        } handledBy addKeywordHandler
//                    }
//                }
//            }
//        }
//    }
//}
